import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchTip, fetchTips, updateTip, createTip, destroyTip, reorderTips } from "../../actions/mine_craft/tipsActions";

export interface TipInterface {
  url: string;

  id: number;
  entity_id: number;
  title: string;
  title_translations: { [key: string]: string | undefined };
  empty_locales: string[];
  order: number;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: TipInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<TipInterface>();

const slice = createSlice({
  name: 'tips',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchTips.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchTips.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.tips;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchTips.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchTip.fulfilled, (state, action) => {
        const tip = Object.values(action.payload.tip)[0];
        state.entities[tip.id] = tip;
      })

      // #create
      .addCase(createTip.fulfilled, (state, action) => {
        const tip = Object.values(action.payload.tip)[0];
        state.entities[tip.id] = tip;
        state.ids.push(tip.id);
      })
      .addCase(createTip.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateTip.fulfilled, (state, action) => {
        const tip = Object.values(action.payload.tip)[0];
        state.entities[tip.id] = tip;
      })
      .addCase(updateTip.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyTip.fulfilled, (state, action) => {
        adaptor.removeOne(state, action.payload.id);
      })

      // #reorder
      .addCase(reorderTips.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectTipById,
  selectIds: selectTipIds,
  selectEntities: selectTipEntities,
  selectAll: selectAllTips,
  selectTotal: selectTotalTips
} = adaptor.getSelectors<RootState>(state => state.mineCraft.tips);
